import { AccessControl, NativeBiometric } from '@capgo/capacitor-native-biometric';

import { resultadoDeError, TextosDeVerificacion, verificarIdentidad } from './biometria';
import { esApp, esIphone } from './plataforma';

/**
 * El NIF y la contraseña guardados en la caja fuerte del móvil (Llavero en iPhone, Keystore en
 * Android), atados a la cara o la huella: solo salen tras confirmarla. Arca no los guarda en
 * ningún otro sitio. Es lo que permite «entrar con Face ID» sin tocar la API.
 *
 * Atados a la biometría ACTUAL (`BIOMETRY_CURRENT_SET`): si alguien añade otra cara o huella al
 * móvil, el sistema los invalida y hay que volver a entrar con contraseña.
 *
 * En la web no hace nada.
 */

/** Con qué nombre se guardan en la caja fuerte. */
const SERVIDOR = 'app.arcadigital.app';

export interface AccesoGuardado {
	nif: string;
	contrasena: string;
}

/**
 * Guarda el acceso. Android pide la huella al guardar; iPhone no, así que se pide la cara antes
 * para que activarlo se sienta igual en los dos. Responde si quedó guardado.
 */
export const guardarAcceso = async (
	{ nif, contrasena }: AccesoGuardado,
	textos: TextosDeVerificacion
): Promise<boolean> => {
	if (!esApp()) {
		return false;
	}

	if (esIphone() && (await verificarIdentidad(textos)) !== 'ok') {
		return false;
	}

	try {
		await NativeBiometric.setCredentials({
			server: SERVIDOR,
			username: nif,
			password: contrasena,
			accessControl: AccessControl.BIOMETRY_CURRENT_SET,
			title: textos.titulo,
			negativeButtonText: textos.cancelar
		});

		return true;
	} catch {
		return false;
	}
};

/**
 * Pide la cara o la huella y devuelve el acceso. `'cancelado'` si el usuario no quiso (lo
 * guardado sigue valiendo); `null` si ya no hay nada utilizable (nada guardado, invalidado por un
 * cambio de biometría, o un error).
 */
export const recuperarAcceso = async (
	textos: TextosDeVerificacion
): Promise<AccesoGuardado | 'cancelado' | null> => {
	if (!esApp()) {
		return null;
	}

	try {
		const { username, password } = await NativeBiometric.getSecureCredentials({
			server: SERVIDOR,
			reason: textos.motivo,
			title: textos.titulo,
			negativeButtonText: textos.cancelar
		});

		return { nif: username, contrasena: password };
	} catch (error) {
		// Cancelar no invalida nada: lo guardado sigue ahí para la próxima vez.
		return resultadoDeError(error) === 'cancelado' ? 'cancelado' : null;
	}
};

/** Lo borra de la caja fuerte. Si no había nada, no pasa nada. */
export const borrarAcceso = async (): Promise<void> => {
	if (!esApp()) {
		return;
	}

	try {
		await NativeBiometric.deleteCredentials({ server: SERVIDOR });
	} catch {
		/* no había nada guardado: ya está como se quería */
	}
};
